import { Link, type LinkProps } from 'react-router-dom'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './utils'

type ButtonVariant = 'primary' | 'secondary' | 'solid' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

const variantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/30 hover:from-violet-500 hover:to-indigo-500 focus-visible:outline-violet-500 disabled:from-zinc-400 disabled:to-zinc-500 disabled:shadow-none dark:shadow-violet-900/40',
  secondary:
    'border-2 border-violet-500/80 bg-white text-violet-700 shadow-sm hover:bg-violet-50 focus-visible:outline-violet-500 disabled:border-zinc-300 disabled:text-zinc-400 dark:border-violet-400/60 dark:bg-zinc-900 dark:text-violet-300 dark:hover:bg-violet-950/50 dark:disabled:border-zinc-600',
  solid:
    'bg-violet-600 text-white shadow-lg hover:bg-violet-500 focus-visible:outline-violet-500 disabled:opacity-60',
  ghost:
    'border border-zinc-300 bg-white text-zinc-800 shadow-sm hover:bg-zinc-50 focus-visible:outline-zinc-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700',
  danger:
    'border border-red-300 bg-red-50 text-red-800 shadow-sm hover:bg-red-100 focus-visible:outline-red-500 dark:border-red-800/80 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/40',
}

const sizeClass: Record<ButtonSize, string> = {
  sm: 'rounded-xl px-3 py-2 text-sm',
  md: 'rounded-2xl px-5 py-3 text-sm',
  lg: 'rounded-2xl px-6 py-4 text-lg',
}

type SharedButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  icon?: ReactNode
  iconPosition?: 'start' | 'end'
}

function buttonContent(
  children: ReactNode,
  icon?: ReactNode,
  iconPosition: 'start' | 'end' = 'start',
) {
  if (!icon) return children
  const iconEl = (
    <span className="inline-flex shrink-0 [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
  )
  if (iconPosition === 'end') {
    return (
      <>
        {children}
        {iconEl}
      </>
    )
  }
  return (
    <>
      {iconEl}
      {children}
    </>
  )
}

function buttonClass({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: SharedButtonProps & { className?: string }) {
  return cx(
    'inline-flex items-center justify-center gap-1.5 font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed',
    fullWidth && 'w-full',
    variantClass[variant],
    sizeClass[size],
    className,
  )
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  SharedButtonProps

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  type = 'button',
  icon,
  iconPosition = 'start',
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, size, fullWidth, className })}
      {...props}
    >
      {buttonContent(children, icon, iconPosition)}
    </button>
  )
}

export type ButtonLinkProps = LinkProps &
  SharedButtonProps & {
    children: ReactNode
  }

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  icon,
  iconPosition = 'start',
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <Link
      className={buttonClass({ variant, size, fullWidth, className })}
      {...props}
    >
      {buttonContent(children, icon, iconPosition)}
    </Link>
  )
}
